/**
 * useApi Hook
 * 自定義 React Hook 用於處理 API 請求
 * 提供 loading、error、data 狀態管理
 */

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useApi Hook
 * @param {Function} apiFunc - API 請求函數
 * @param {Object} options - 選項
 * @param {boolean} options.immediate - 是否立即執行（預設 true）
 * @param {Function} options.onSuccess - 成功回調
 * @param {Function} options.onError - 錯誤回調
 * @returns {Object} - { data, loading, error, execute, reset }
 */
export const useApi = (apiFunc, options = {}) => {
  const {
    immediate = false,
    onSuccess = null,
    onError = null
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  // 清理函數
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * 執行 API 請求
   */
  const execute = useCallback(async (...args) => {
    try {
      setLoading(true);
      setError(null);

      const result = await apiFunc(...args);

      if (isMountedRef.current) {
        setData(result);
        setLoading(false);

        if (onSuccess) {
          onSuccess(result);
        }

        return result;
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err);
        setLoading(false);

        if (onError) {
          onError(err);
        }

        throw err;
      }
    }
  }, [apiFunc, onSuccess, onError]);

  /**
   * 重置狀態
   */
  const reset = useCallback(() => {
    if (isMountedRef.current) {
      setData(null);
      setError(null);
      setLoading(false);
    }
  }, []);

  // 如果 immediate 為 true，立即執行
  useEffect(() => {
    if (immediate) {
      execute();
    }
    // Note: execute is intentionally omitted from deps to prevent infinite loops
    // eslint-disable-next-line
  }, [immediate]);

  return { data, loading, error, execute, reset };
};

/**
 * useApiMutation Hook (for POST, PUT, DELETE)
 * 專門用於修改操作的 Hook
 * @param {Function} apiFunc - API 請求函數
 * @param {Object} options - 選項
 * @returns {Object} - { mutate, loading, error, success, reset }
 */
export const useApiMutation = (apiFunc, options = {}) => {
  const {
    onSuccess = null,
    onError = null
  } = options;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * 執行變更操作
   */
  const mutate = useCallback(async (...args) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const result = await apiFunc(...args);

      if (isMountedRef.current) {
        setLoading(false);
        setSuccess(true);

        if (onSuccess) {
          onSuccess(result);
        }

        return result;
      }
    } catch (err) {
      if (isMountedRef.current) {
        setLoading(false);
        setError(err);

        if (onError) {
          onError(err);
        }

        throw err;
      }
    }
  }, [apiFunc, onSuccess, onError]);

  /**
   * 重置狀態
   */
  const reset = useCallback(() => {
    if (isMountedRef.current) {
      setError(null);
      setLoading(false);
      setSuccess(false);
    }
  }, []);

  return { mutate, loading, error, success, reset };
};

/**
 * useApiList Hook
 * 專門用於列表數據的 Hook，支援分頁和刷新
 * @param {Function} apiFunc - API 請求函數
 * @param {Object} options - 選項
 * @returns {Object} - { data, loading, error, refresh, loadMore, hasMore }
 */
export const useApiList = (apiFunc, options = {}) => {
  const {
    immediate = true,
    pageSize = 20
  } = options;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * 載入數據
   */
  const loadData = useCallback(async (pageNum, append = false) => {
    try {
      setLoading(true);
      setError(null);

      const result = await apiFunc({
        offset: pageNum * pageSize,
        limit: pageSize
      });

      if (isMountedRef.current) {
        const newData = result.data || result.posts || result.items || [];

        if (append) {
          setData(prev => [...prev, ...newData]);
        } else {
          setData(newData);
        }

        setHasMore(newData.length === pageSize);
        setLoading(false);
        return newData;
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err);
        setLoading(false);
        throw err;
      }
    }
  }, [apiFunc, pageSize]);

  /**
   * 刷新列表（重新載入第一頁）
   */
  const refresh = useCallback(async () => {
    setPage(0);
    return await loadData(0, false);
  }, [loadData]);

  /**
   * 載入更多（下一頁）
   */
  const loadMore = useCallback(async () => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      return await loadData(nextPage, true);
    }
  }, [page, loading, hasMore, loadData]);

  // 初始載入
  useEffect(() => {
    if (immediate) {
      loadData(0, false);
    }
    // Note: loadData is intentionally omitted from deps to prevent infinite loops
    // eslint-disable-next-line
  }, [immediate]);

  return { data, loading, error, refresh, loadMore, hasMore };
};

export default {
  useApi,
  useApiMutation,
  useApiList
};
